import { Employee } from '@thera-hr/check-api-sdk/api';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { navigateBack } from 'utils/navigation';

import CloseModalButton from 'components/app-components/modals/CloseModalButton';
import Navbar from 'components/container-components/navbar/Navbar';
import { NavigationProvider } from 'components/container-components/navbar/NavigationContext';
import Banner from 'components/generic-components/banner/Banner';
import ReferralBanner, { isReferralBannerVisible } from 'components/generic-components/banner/ReferralBanner';
import Button from 'components/app-components/ui/button';
import Footer from 'components/generic-components/thera-start-components/Footer';
import TheraStartNavbar from 'components/generic-components/thera-start-components/Navbar';
import TopBar from 'components/generic-components/thera-start-components/TopBar';
import { useCompany } from 'context/company';
import { useUser } from 'context/user';
import { useCheckEmployeeSWR } from 'hooks/useCheckEmployeeSWR';
import { usePermissions } from 'hooks/usePermissions';
import usePreviousUrl from 'hooks/usePreviousUrl';
import { AdminRoutes, AuthRoutes, CommonRoutes, ContractorRoutes, isAlwaysPublicRoute } from 'routes';
import { checkIfManagerWithoutTeams } from 'utils/affiliations';
import FallbackUI from '../FallbackUI';
import LoadingScreen from '../LoadingScreen';
import SwitchAccountModal from '../navbar/SwitchAccountModal';
import NoContent from '../NoContent';
import { isWhiteLabelEnabled } from 'utils/whiteLabelConfig';
import BackCaretArrow from 'icons/BackCaretArrow';

// Routes that don't use the standard layout components
const nonLayoutRoutes = [
  '/',
  ContractorRoutes.EmployeeOnboarding,
  AdminRoutes.PayrollOnboarding,
  ...Object.values(AuthRoutes),
  AdminRoutes.InvoiceTemplatesPreview,
  AdminRoutes.TPOnboarding,
  ContractorRoutes.WalletOnboarding,
  CommonRoutes.Terms,
];

// Returns route configuration for pages that use the InvoicesLayout component
// These are typically forms, modals, and focused workflows that need a simplified header
const getInvoiceLayoutRoutes = (checkEmployee?: Employee) => {
  const invoiceSpecificRoutes = [
    { route: AdminRoutes.PayApprovedInvoices, homeRoute: AdminRoutes.Contractors, hasCloseButton: true },
    { route: AdminRoutes.ReviewInvoices, homeRoute: AdminRoutes.Contractors, hasCloseButton: true },
    { route: AdminRoutes.NotDueInvoices, homeRoute: AdminRoutes.Contractors, hasCloseButton: true },
    { route: AdminRoutes.ReviewTimeOffs, homeRoute: AdminRoutes.Contractors, hasCloseButton: true },
    { route: AdminRoutes.ReviewExpenses, homeRoute: AdminRoutes.Contractors, hasCloseButton: true },
    {
      route: AdminRoutes.QBIntegrationsConnect,
      homeRoute: AdminRoutes.QBIntegrations,
      title: 'Connect QuickBooks',
      hasCloseButton: true,
      editPageTitle: 'Edit QuickBooks',
      editPageQuery: AdminRoutes.QBIntegrationsEdit?.split(/[?&=]/)[1],
    },
    { route: AdminRoutes.AddTeam, hasCloseButton: true },
    { route: AdminRoutes.ManageTeam, hasCloseButton: true },
    { route: AdminRoutes.EditContract, homeRoute: AdminRoutes.Team, title: 'Edit Contract', hasCloseButton: true },
    { route: AdminRoutes.EndContract, homeRoute: AdminRoutes.Team, title: 'End Contract', hasCloseButton: true },
    { route: AdminRoutes.EndContractEOR, homeRoute: AdminRoutes.Team, title: 'End Employment', hasCloseButton: true },
    { route: AdminRoutes.BulkContractorUpload, fullWidthHeader: true, fullWidthMain: true },
    { route: AdminRoutes.IRSFiling, hasCloseButton: true, title: 'File 1099-NEC to the IRS' },
    {
      route: AdminRoutes.TerminateEmployment,
      hasCloseButton: true,
      title: checkEmployee ? `Terminate ${checkEmployee?.firstName} ${checkEmployee?.lastName}` : 'Terminate Employee',
    },
    {
      route: AdminRoutes.EditCompensation,
      hasCloseButton: true,
      title: checkEmployee
        ? `Edit Pay for ${checkEmployee?.firstName} ${checkEmployee?.lastName}`
        : 'Edit Employee Pay',
    },
  ];

  return invoiceSpecificRoutes;
};

// Standard layout with full navigation bar and potential onboarding banners
const NormalLayout = ({ children }) => {
  const router = useRouter();
  const { company } = useCompany();
  const { currentCompany, roleType } = useUser();
  const isManager = roleType === 'manager';
  // Company location and banking checks for onboarding flow
  const isUSCompany = company?.address?.country === 'US';
  // Check for callerUserBeneficiaryInformation to ensure beneficiary onboarding is complete
  const isBankingDetailsSubmitted =
    isUSCompany && Boolean(currentCompany?.companyBankingDetails?.callerUserBeneficiaryInformation);
  // if its incorporation company then we need to check if incorporation details are complete otherwise true for others
  const isIncorporationCompleted =
    isUSCompany && Boolean(currentCompany?.incorporationDetails)
      ? currentCompany?.incorporationDetails?.status === 'complete'
      : true;
  //  If company is incorporation company then until their incorporation details are complete, we dont wanna show the banner
  // as for incorporation company as well, isTPOnboardingSkipped is true
  const isOnboardingPending =
    isUSCompany && !isBankingDetailsSubmitted && company?.settings?.isTpOnboardingSkipped && isIncorporationCompleted;
  const isPayrollFBOPending =
    isUSCompany &&
    company?.theraBanking &&
    isBankingDetailsSubmitted &&
    company?.tp &&
    company?.tp?.payrollFboOnboardingStage !== 'DISBURSEMENT_ACCOUNT_APPROVED' &&
    company?.tp?.payrollFboOnboardingStage !== 'SUBMITTED';

  const hasCompanyBanner = isManager && (isOnboardingPending || isPayrollFBOPending);
  const [showReferralBanner, setShowReferralBanner] = useState(isReferralBannerVisible);
  const hasBanner = hasCompanyBanner || showReferralBanner;

  return (
    <NavigationProvider hasBanner={hasBanner}>
      <header className="fixed top-0 z-10 w-[100vw] bg-neutral-800 pt-[var(--safe-top)]">
        {isManager && isOnboardingPending && (
          <Banner
            text={
              <>
                {' '}
                Complete your company sign-up{' '}
                <span className="cursor-pointer underline" onClick={() => router.push(AdminRoutes.TPOnboarding)}>
                  here
                </span>{' '}
              </>
            }
          />
        )}
        {isManager && isPayrollFBOPending && <Banner text="Your application is under review" />}
        {!hasCompanyBanner && <ReferralBanner onVisibilityChange={setShowReferralBanner} />}
        {/* Below a banner the navbar starts past the banner (which already
            clears the notch), so its own safe-area padding is zeroed out */}
        <Navbar containerClassName={hasBanner ? 'mt-[calc(2.25rem+var(--safe-top))] !pt-0' : ''} />
      </header>
      <main className={`${hasBanner ? 'mt-[100px]' : 'mt-16 md:mt-[64px]'} flex flex-1`}>{children}</main>
    </NavigationProvider>
  );
};

const InvoicesLayout = ({
  title,
  homeRoute,
  hasCloseButton,
  onCloseRoute,
  children,
  editPageQuery,
  editPageTitle,
  fullWidthHeader,
  fullWidthMain,
}: {
  title: string;
  homeRoute: string;
  hasCloseButton: boolean;
  onCloseRoute?: string;
  children: React.ReactNode;
  editPageQuery: string;
  editPageTitle: string;
  fullWidthHeader?: boolean;
  fullWidthMain?: boolean;
}) => {
  const router = useRouter();
  const isEditPage = Boolean(router?.query?.[editPageQuery]);

  /**
   * Handles navigation when clicking the Thera logo
   * Uses browser back to return to previous page
   */
  const onBack = () => {
    navigateBack(router, homeRoute || AdminRoutes.Dashboard);
  };

  const isWhiteLabel = isWhiteLabelEnabled();

  return (
    <NavigationProvider>
      <header className="fixed top-0 z-[20] flex h-[calc(64px+var(--safe-top))] w-[100vw] items-center border-b border-neutral-200 bg-neutral-800 px-6 pt-[var(--safe-top)]">
        <div className={`flex flex-1 items-center justify-between ${fullWidthHeader ? '' : 'mx-auto max-w-screen-xl'}`}>
          <div className="flex items-center">
            <Button variant="plain" onClick={onBack}>
              {isWhiteLabel ? (
                <BackCaretArrow stroke="#FFFFFF" scale={2} />
              ) : (
                <img className="h-6 w-auto" src="/logo/thera-logo-white-new.svg" alt="Thera-logo" />
              )}
            </Button>
            {title && (
              <>
                <div className="mx-6 h-7 w-[1px] bg-neutral-500" />
                <p className="text-base font-medium text-white"> {isEditPage ? editPageTitle : title}</p>
              </>
            )}
          </div>
          {hasCloseButton && (
            <CloseModalButton
              className="text-white hover:bg-transparent hover:text-white"
              onClose={() => {
                onCloseRoute ? router.push(onCloseRoute) : navigateBack(router, homeRoute || AdminRoutes.Dashboard);
              }}
            />
          )}
        </div>
      </header>
      <main className={fullWidthMain ? 'mt-[64px]' : 'mt-[64px] px-4 md:px-6'}>
        {fullWidthMain ? children : <div className="mx-auto w-full max-w-screen-xl">{children}</div>}
      </main>
    </NavigationProvider>
  );
};

// Marketing/landing page layout with custom fonts and footer
const TheraStartLayout = ({ children }) => {
  return (
    <div
      className="font-sofia flex min-h-screen flex-col"
      style={{ fontFamily: "'Sofia Pro', Arial, Helvetica, sans-serif" }}
    >
      <TopBar />
      <TheraStartNavbar />
      <main className="flex w-full flex-1 flex-col">{children}</main>
      <Footer />
    </div>
  );
};

/**
 * Main layout component that determines which layout variant to use
 * based on the current route and user state
 */
export default function Layout({ children }) {
  const {
    roleType,
    affiliationsByCompany,
    currentCompany,
    isLoading: isUserLoading,
    isLoggingOut,
    showAccountSwitcher,
    setShowAccountSwitcher,
    is2FAVerified,
    isUserProfileReady,
    hasNoAffiliations,
    hasAffiliationsError,
  } = useUser();
  const { isOrgAdmin, isAPARManager, isHRSpecialist } = usePermissions();
  const isOrgAdminOrHRSpecialist = isOrgAdmin || isHRSpecialist;
  const router = useRouter();
  const isTheraStartRoute = router.pathname.includes(AuthRoutes.TheraStart);
  const employeeID = router.query.employeeID as string; // only exists for W2 specific pages
  const { checkEmployee, isPending: isCheckEmployeeLoading } = useCheckEmployeeSWR({ checkEmployeeId: employeeID });
  // Layout state management - determines which layout variant to render
  const [isNormalLayoutRoute, setNormalLayoutRoute] = useState(() => {
    return !nonLayoutRoutes.includes(router.pathname);
  });
  const [isInvoiceLayoutRoute, setIsInvoiceLayoutRoute] = useState(() => {
    return getInvoiceLayoutRoutes(checkEmployee).find((routeDetails) => routeDetails.route === router.pathname);
  });
  // User permission and role checks
  const isManagerWithoutTeams = checkIfManagerWithoutTeams(
    roleType,
    affiliationsByCompany,
    currentCompany,
    isOrgAdminOrHRSpecialist,
  );
  const isAccountSecurityRoute = router.pathname.includes(AdminRoutes.SecuritySettings);
  const isSettingsRoute = router.pathname.includes('account');
  const isAPARRoute = router.pathname.includes('ap-ar');
  const isVerifyEmailRoute = router.pathname.includes(AuthRoutes.EmailVerification);
  const isAPARManagerRoute = isAPARManager ? isAPARRoute : false;
  const isFinancialAppRoute = ['/ar', '/ap', '/banking'].some(
    (prefix) => router.pathname === prefix || router.pathname.startsWith(`${prefix}/`),
  );
  const previousPageUrl = usePreviousUrl();
  const isLoading = isUserLoading || isCheckEmployeeLoading || isLoggingOut;

  // Update layout type when route changes
  useEffect(() => {
    setNormalLayoutRoute(!nonLayoutRoutes.includes(router.pathname));
    setIsInvoiceLayoutRoute(
      getInvoiceLayoutRoutes(checkEmployee).find((routeDetails) => routeDetails.route === router.pathname),
    );
  }, [router.pathname, checkEmployee]);

  // Before every account gate below: a visitor whose access was revoked still has to see these.
  if (isAlwaysPublicRoute(router.pathname)) {
    return children;
  }

  // Loading state (skip for security settings to prevent infinite loading)
  if (isLoading && !isAccountSecurityRoute) {
    return <LoadingScreen />;
  }

  // Only show account switcher after user profile is fully ready and 2FA is verified
  // This prevents the switcher from appearing prematurely during 2FA completion
  if (showAccountSwitcher && is2FAVerified && isUserProfileReady) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <SwitchAccountModal isOpen={showAccountSwitcher} onClose={() => setShowAccountSwitcher(false)} />
      </div>
    );
  }

  // Backend error state: affiliations response was malformed (null / missing field)
  if (hasAffiliationsError) {
    return <FallbackUI />;
  }

  // Error state: User has no company affiliations (access revoked)
  // Super-admin page is exempt: admins need it to perform ops tasks (e.g. reset MFA) on users with no affiliations.
  if (hasNoAffiliations && router.pathname !== AuthRoutes.SuperAdmin) {
    return (
      <NormalLayout>
        <NoContent
          title="Access revoked"
          subTitle="Your organization has revoked your access. Please contact your organization admin to restore it."
          description="Once your access is restored, you'll be able to sign in to your Thera dashboard."
        />
      </NormalLayout>
    );
  }

  // Manager without teams - show empty state except for specific routes
  if (isManagerWithoutTeams && !isAPARManagerRoute && !isSettingsRoute && !isVerifyEmailRoute && !isFinancialAppRoute) {
    return (
      <NormalLayout>
        <NoContent />
      </NormalLayout>
    );
  }

  // Invoice/form layout with simplified header and back navigation
  if (isInvoiceLayoutRoute) {
    return (
      <InvoicesLayout
        title={isInvoiceLayoutRoute.title}
        homeRoute={previousPageUrl || isInvoiceLayoutRoute.homeRoute}
        hasCloseButton={isInvoiceLayoutRoute.hasCloseButton}
        editPageQuery={isInvoiceLayoutRoute?.editPageQuery}
        editPageTitle={isInvoiceLayoutRoute?.editPageTitle}
        fullWidthHeader={isInvoiceLayoutRoute?.fullWidthHeader}
        fullWidthMain={isInvoiceLayoutRoute?.fullWidthMain}
      >
        {children}
      </InvoicesLayout>
    );
  }

  // Marketing/landing pages
  if (isTheraStartRoute) {
    return <TheraStartLayout>{children}</TheraStartLayout>;
  }

  // Standard app layout with navigation
  if (isNormalLayoutRoute) {
    return <NormalLayout>{children}</NormalLayout>;
  }

  // No layout wrapper (for special pages like auth)
  return children;
}
